import {type Camera, EventDispatcher,} from 'three';
import type {StandardViewType} from '../view-orientation/standardViewTypes';
import type {ViewportGizmoEventMap} from './viewport-gizmo-events';
import {ViewportGizmoInteractionController} from './ViewportGizmoInteractionController';
import {ViewportGizmoRenderer, type ViewportGizmoRendererOptions,} from './ViewportGizmoRenderer';


/** 视口方向导航器公开配置。 */
export type ViewportGizmoOptions = ViewportGizmoRendererOptions;

/**
 * 组合方向导航器渲染、交互和公开事件的公共门面。
 *
 * 消费方只需管理启动、逐帧渲染和释放，并通过 Three.js
 * `EventDispatcher` 监听用户意图，不需要持有内部场景或 DOM 监听器。
 */
export class ViewportGizmo extends EventDispatcher<ViewportGizmoEventMap> {
  /** 独立 WebGL 场景与 DOM 的渲染器。 */
  private readonly renderer: ViewportGizmoRenderer;

  /** 将 DOM 指针流转换为公开导航器事件的控制器。 */
  private readonly interactionController: ViewportGizmoInteractionController;

  /** 是否已经释放。 */
  private disposed = false;

  /**
   * 创建并挂载视口方向导航器。
   *
   * @param options 容器、尺寸、边距与像素比配置。
   */
  public constructor(options: ViewportGizmoOptions) {
    super();
    this.renderer = new ViewportGizmoRenderer(options);
    this.interactionController = new ViewportGizmoInteractionController({
      gizmo: this.renderer,
      eventDispatcher: this,
    });
  }

  /** 获取接收导航器指针事件的根 DOM。 */
  public get domElement(): HTMLDivElement {
    return this.renderer.domElement;
  }

  /**
   * 启动导航器原生指针监听；重复调用保持幂等。
   *
   * @throws 当导航器已经释放时抛出错误。
   */
  public start(): void {
    if (this.disposed) {
      throw new Error('视口方向导航器已释放，不能重新启动。');
    }

    this.interactionController.start();
  }

  /**
   * 同步主相机姿态并渲染独立导航器场景。
   *
   * @param mainCamera 当前活动的 Three.js 主相机。
   * @param activeViewType 当前匹配的标准视图。
   */
  public render(
    mainCamera: Camera,
    activeViewType: StandardViewType | null,
  ): void {
    if (this.disposed) {
      return;
    }

    this.renderer.render(mainCamera, activeViewType);
  }

  /** 解除交互监听并释放 DOM、场景和 WebGL 资源。 */
  public dispose(): void {
    if (this.disposed) {
      return;
    }

    this.disposed = true;
    this.interactionController.dispose();
    this.renderer.dispose();
  }
}

